import { Link } from "@tanstack/react-router";

import { PAYMENT_METHODS } from "@/lib/data";
import { methodTestStats } from "@/lib/testdata";
import { MethodLogo } from "@/components/method-logo";

/**
 * Vergelijking van de huidige betaalmethode met de andere geteste methoden,
 * volledig gebaseerd op onze eigen meetreeks.
 */
export function MethodComparison({ methodSlug }: { methodSlug: string }) {
  const rows = PAYMENT_METHODS.map((m) => ({ method: m, stats: methodTestStats(m.slug) }))
    .filter((r) => r.stats.tests > 0)
    .sort((a, b) => (a.stats.medianDepositSeconds ?? 999) - (b.stats.medianDepositSeconds ?? 999));

  const current = rows.find((r) => r.method.slug === methodSlug);
  if (!current) return null;
  const position = rows.indexOf(current) + 1;

  return (
    <section aria-labelledby="vergelijking" className="scroll-mt-28">
      <h2 id="vergelijking" className="text-xl font-semibold text-foreground">
        {current.method.name} vergeleken met andere betaalmethoden
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        Op mediane stortingssnelheid staat {current.method.name} op plaats {position} van de{" "}
        {rows.length} methoden die wij meten. De tabel toont dezelfde meting voor elke methode, zodat
        je ziet wat je wint of verliest door over te stappen.
      </p>
      <div className="mt-5 overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[640px] text-left text-sm">
          <caption className="sr-only">Mediane meetwaarden per betaalmethode</caption>
          <thead className="bg-secondary/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th scope="col" className="px-4 py-3 font-medium">Methode</th>
              <th scope="col" className="px-4 py-3 font-medium">Storting (mediaan)</th>
              <th scope="col" className="px-4 py-3 font-medium">Uitbetaling (mediaan)</th>
              <th scope="col" className="px-4 py-3 font-medium">KYC-vraag</th>
              <th scope="col" className="px-4 py-3 font-medium">Tests</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(({ method, stats }) => {
              const isCurrent = method.slug === methodSlug;
              return (
                <tr key={method.slug} className={isCurrent ? "bg-secondary/40" : "bg-card"}>
                  <th scope="row" className="px-4 py-3 font-medium text-foreground">
                    <span className="flex items-center gap-2.5">
                      <MethodLogo slug={method.slug} name={method.name} size="sm" />
                      {isCurrent ? (
                        method.name
                      ) : (
                        <Link
                          to="/betaalmethoden/$slug/"
                          params={{ slug: method.slug }}
                          className="underline underline-offset-2 hover:text-primary"
                        >
                          {method.name}
                        </Link>
                      )}
                    </span>
                  </th>
                  <td className="px-4 py-3 text-muted-foreground">{stats.medianDepositSeconds} sec.</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {!stats.withdrawalSupportedInTests
                      ? "Via bank/wallet"
                      : `${stats.medianWithdrawalHours} uur${method.withdrawal_supported ? "" : " (naar bank)"}`}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{stats.kycRate}%</td>
                  <td className="px-4 py-3 text-muted-foreground">{stats.tests}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
